import { escapeRegExp, toString, uniq } from "lodash-es";

import { stockAliases } from "shared/constants";
import { isNonNullable } from "shared/types/guards";
import { type Nullable, type Nullish } from "shared/types/utility";

export function humanize(value: undefined): undefined;
export function humanize(value: null): null;
export function humanize(value: 0): 0;
export function humanize(value: ""): "";
export function humanize<T extends string>(value: T): string;

export function humanize<T extends string>(
  value: Nullish<T> | 0
): Nullish<string> | 0 {
  if (!value) {
    return value as Nullish<string> | 0;
  }

  return value.replace(/[-_]/g, " ");
}

export function pluralize(value: number, ...args: string[]): string {
  return args.length > 1
    ? args[value % (10 - 1)] || args[args.length - 1]
    : args[0] + (value === 1 ? "" : "s");
}

export function capitalize(value: undefined): undefined;
export function capitalize(value: null): null;
export function capitalize(value: 0): 0;
export function capitalize(value: ""): "";
export function capitalize(value: string): string;

export function capitalize(value: Nullish<string> | 0): Nullish<string> | 0 {
  if (!value) return value as Nullish<string> | 0;

  return value.replace(/\b\w/g, (string) => string.toUpperCase());
}

export function capitalizeName(value: undefined): undefined;
export function capitalizeName(value: null): null;
export function capitalizeName(value: 0): 0;
export function capitalizeName(value: ""): "";
export function capitalizeName(value: string): string;

export function capitalizeName(
  value: Nullish<string> | 0
): Nullish<string> | 0 {
  if (!value) return value as Nullish<string> | 0;

  // Short names are most likely abbreviations like AAP or ABC
  if (value.length < 5) return value;

  // Names containing more than 1 period are likely intentional
  const periodMatches = value.match(/\./g);
  if (periodMatches && periodMatches.length > 1) return value;

  if (value === value.toLowerCase() || value === value.toUpperCase()) {
    return capitalize(value.toLowerCase());
  }

  return value;
}

export function parseDocumentId(value: string): {
  id: string;
  type: string;
} {
  // Split any length of underscores by its last underscore
  const [type, , id] = value.split(/(_(?=[^_]*$))/);

  return { id, type };
}

export function upperCase(value: undefined): undefined;
export function upperCase(value: null): null;
export function upperCase(value: 0): 0;
export function upperCase(value: ""): "";
export function upperCase(value: string): string;

export function upperCase(value: Nullish<string> | 0): Nullish<string> | 0 {
  if (!value) {
    return value as Nullish<string> | 0;
  }

  return value.toUpperCase();
}

export function lowerCase(value: undefined): undefined;
export function lowerCase(value: null): null;
export function lowerCase(value: 0): 0;
export function lowerCase(value: ""): "";
export function lowerCase(value: string): string;

export function lowerCase(value: Nullish<string> | 0): Nullish<string> | 0 {
  if (!value) return value as Nullish<string> | 0;

  return value.toLowerCase();
}

export function constantize(value: undefined): undefined;
export function constantize(value: null): null;
export function constantize(value: 0): 0;
export function constantize(value: ""): "";
export function constantize(value: string): string;

export function constantize(value: Nullish<string> | 0): Nullish<string> | 0 {
  if (!value) return value as Nullish<string> | 0;

  return value
    .split("_")
    .map((item) => [item.charAt(0).toUpperCase(), item.substring(1)].join(""))
    .join("");
}

type Singularize<T extends string> = T extends `${infer Rest}s` ? Rest : T;

export function singularize<T extends string>(value: T): Singularize<T> {
  return value.slice(0, -1) as Singularize<T>;
}

export function nl2br(
  value: Nullish<string> | 0,
  maxNewLines: Nullable<number> = null
): Nullable<string> {
  if (!value) return null;

  let output = value;

  if (maxNewLines) {
    const newLineRegex = new RegExp(
      `((\r\n|\n\r|\r|\n){${maxNewLines},})`,
      "g"
    );

    output = output.replace(newLineRegex, "\n".repeat(maxNewLines));
  }

  return output.replace(/(\r\n|\n\r|\r|\n)/g, "<br /><br />");
}

type PrettyTypeReturn =
  | "Online"
  | "Print"
  | "Magazine"
  | "Podcast"
  | "TV"
  | "Radio"
  | "Social"
  | "TvSegment"
  | "RadioSegment"
  | "TranscriptRequest"
  | "Journalist"
  | "ExternalItem"
  | "";

export function prettyType(value: unknown): PrettyTypeReturn {
  if (typeof value !== "string") {
    return "";
  }

  switch (value) {
    case "article":
    case "online":
      return "Online";
    case "paper_article":
      return "Print";
    case "magazine_article":
    case "magazine":
      return "Magazine";
    case "podcast_episode":
    case "podcast":
      return "Podcast";
    case "caption":
    case "tv_caption":
    case "tv_story":
    case "tv_super":
    case "tv_logo_appearance":
    case "tv":
      return "TV";
    case "radio_clip":
    case "radio_article":
    case "radio_story":
    case "radio":
      return "Radio";
    case "tweet":
    case "facebook_post":
    case "youtube_video":
    case "instagram_post":
    case "instagram_comment":
    case "exorde_reddit_post":
    case "exorde_reddit_post_comment":
    case "reddit_post":
    case "reddit_post_comment":
    case "blog_post":
    case "forum_post":
    case "bluesky_post":
    case "linkedin_post":
    case "linkedin_comment":
    case "tiktok_comment":
    case "tiktok_video":
    case "social":
      return "Social";
    case "tv_segment":
    case "tv_media_request":
      return "TvSegment";
    case "radio_segment":
    case "radio_media_request":
      return "RadioSegment";
    case "transcript_request":
      return "TranscriptRequest";
    case "journalist":
      return "Journalist";
    case "external_item":
      return "ExternalItem";
    default:
      return "";
  }
}

export function cleanStockSymbol(symbol: Nullish<string> | 0): string {
  if (!symbol) return "";

  const regexString = Object.keys(stockAliases)
    .map((alias) => alias.replace("^", "\\^"))
    .join("|");

  const aliasRegex = new RegExp(`^(${regexString})$`);

  return symbol
    .replace(/\.\w+$/, "")
    .replace(
      aliasRegex,
      (_match, group) => stockAliases[group as keyof typeof stockAliases]
    );
}

export function highlight(
  text: Nullish<string>,
  keywords: Nullish<string[]>
): string {
  if (!isNonNullable(text)) return "";

  if (!keywords || !keywords.length) return text;

  // filter out empty keywords, sort longest first
  const filteredKeywords = keywords
    .filter((keyword) => Boolean(keyword))
    .sort((keywordA, keywordB) => keywordB.length - keywordA.length)
    .map((keyword) => escapeRegExp(keyword))
    .join("|");

  // don't highlight if filtered keywords is empty
  if (!filteredKeywords) return text;

  const regexp = new RegExp(`\\b(${filteredKeywords})\\b`, "gi");

  return text.replace(regexp, "<em>$1</em>");
}

export function truncateWords(text: string, length: number): string {
  if (text.indexOf(" ") === -1) return `${text.substring(0, 35)}...`;

  const words = text.split(" ");

  return words.length > length
    ? `${words.slice(0, length).join(" ")}...`
    : text;
}

export function extractKeywords(text: Nullish<string> | 0): string[] {
  if (!text) {
    return [];
  }

  const fieldsToKeepForHighlight = [
    "text.case_sensitive",
    "text",
    "title",
    "tv_super",
  ];

  const fieldsToKeepRegex = new RegExp(
    `(${fieldsToKeepForHighlight.join("|")}):`,
    "ig"
  );

  const keywordMatches = text
    .replace(/\s+/g, " ") // clean up whitespace
    .replace(/NOT \(?[a-zA-Z0-9:$ "()]*"*\)/g, "") // nested NOT (keyword/"keyword"/AND/OR)
    .replace(/NOT (\(.+\)|"[\w ]+"|\w+)/g, "") // NOT "keyword"/keyword
    .replace(fieldsToKeepRegex, "") // remove field names we want to keep keywords for
    .replace(
      /[a-zA-Z]+?\.?[a-zA-Z_]*?: ?("?(\d+|[a-zA-Z0-9]+_?[a-zA-Z_0-9]*)"?|\(.+?\))/g,
      ""
    ) // remove non-highlight fields
    .replace(/\(|\)|AND|OR/g, "") // nested AND/OR
    .replace(/,(?!(\d))/, "") // comma separated not followed by digits
    .replace(/\(|\)|((NEAR|ONEAR|FIRST)\/[0-9]+)/g, "") // nested NEAR/ONEAR/FIRST operators
    .replace(
      /(-"[a-zA-Z0-9:$\- ]+"|-[a-zA-Z0-9:$-]+(?=(?:[^"]|"[^"]*")*$))/g,
      ""
    ) // remove keywords prefixed with "-" operator
    .replace(/\+/g, "") // remove "+" operator from keywords
    .replace(/\/\*(.|\n)*?\*\//g, "") // remove comments
    .match(/(?:")(.+?)(?=")|(?:[^"]|\s|^)(\b.+?)(?=\s|$)/g); // extract keywords

  const keywords = [...(keywordMatches || [])]
    .flat()
    .map((word) => word.replace(/"/g, "").trim()) // remove grouping quotes
    .filter(
      (word) =>
        // remaining keywords containing special characters used in query and all numerical keywords
        // or are in reference to media types
        word.length &&
        !word.match(
          /((\w+([:]+\w*))|^\d+$|caption|tv_super|article|radio_clip|tweet|paper_article)/i
        )
    );

  return uniq(keywords);
}

export function truncate(text: string, maxLength = 65): string {
  const trimmedText = text.trim();

  if (trimmedText.length <= maxLength) {
    return trimmedText;
  }

  const truncatedText = trimmedText.substring(0, maxLength);

  const nonLetterIndexes = [...truncatedText.matchAll(/[^A-Za-z_]+/g)].map(
    (match) => match.index
  );

  let result: string;

  if (nonLetterIndexes.length === 0) {
    result = truncatedText;
  } else {
    result = truncatedText.substring(
      0,
      nonLetterIndexes[nonLetterIndexes.length - 1]
    );
  }

  return result.concat("...");
}

export function isValidHTMLContent(text: unknown): text is string {
  return (
    typeof text === "string" &&
    Boolean(text.trim().length) &&
    text.trim() !== "<p></p>"
  );
}

export function countWords(text: string): number {
  return text.split(/\s+/)?.filter(Boolean).length ?? 0;
}

export function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function normalizeUrl(unsafe: unknown): string {
  if (typeof unsafe !== "string") {
    return "";
  }

  try {
    return new URL(unsafe).toString();
  } catch {
    return "";
  }
}

export function ensureSafeUrl(value: Nullish<string>): string {
  if (!value) return "";

  const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;

  return normalizeUrl(withProtocol);
}

export function ensurePrefix(value: unknown, prefix = ""): string {
  const stringified = toString(value);

  return stringified.startsWith(prefix) ? stringified : prefix + stringified;
}

export function normalizeForComparison(value?: unknown): string {
  const asString = toString(value);

  return asString.toLowerCase().replace(/\s/g, "");
}

export function splitIntoLines(value: string) {
  return value.split(/\r\n|\n|\r/);
}

// split by comma (,), 'and' and semicolon (;)
const AUTHORS_DELIMITER_REGEX = /\s?(?:,|\band\b|;)\s?/i;

// remove anything after/including for, in, #, -, ? unless its part of a word.
const AUTHORS_CLEANING_REGEX = /\s(for|in|[#\-?]|#.+)(?!\w).*/i;
const AUTHORS_CLEANING_NOISE_REGEX = /^By,/i;

export function splitAuthors(author: string): string[] {
  return author
    .replace(AUTHORS_CLEANING_NOISE_REGEX, "")
    .split(AUTHORS_DELIMITER_REGEX)
    .map((name) => name.replace(AUTHORS_CLEANING_REGEX, ""));
}
